<template>
  <div class="tdt-map" ref="map" style="width: 100%; height: 500px"></div>
</template>

<script>
import loadTDT from "@/utils/loadTDT.js";

export default {
  name: "TdtMap",
  props: {
    tk: { type: String, required: true },
    center: {
      // [lng, lat]
      type: Array,
      default: () => [116.40769, 39.89945], // 北京
    },
    zoom: { type: Number, default: 15 },
    // useImageBase: { type: Boolean, default: false }, // 是否使用影像底图
    markers: {
      // 标记点数组，data用于生成模板
      type: Array,
      default: () => [],
      // 例如：[{ lng: 121.33, lat: 30.74, data: { address: 'xxx', houseDescribe: 1 }}]
    },
  },
  data() {
    return {
      map: null,
      T: null,
      markerList: [],
    };
  },
  async mounted() {
    const T = await loadTDT(this.tk);
    this.T = T;

    this.$nextTick(() => {
      this.initMap();
    });
  },
  methods: {
    initMap() {
      const T = this.T;
      if (!T) return;

      this.map = new T.Map(this.$refs.map, { projection: "EPSG:3857" });

      const center = new T.LngLat(this.center[0], this.center[1]);
      this.map.centerAndZoom(center, this.zoom);
      this.map.enableScrollWheelZoom();

      if (this.markers && this.markers.length) {
        this.addMarkers();
      }

      this.map.addControl(new T.Control.Scale());
      this.map.addControl(new T.Control.Zoom());

      this.map.addEventListener("click", (e) => {
        const { lng, lat } = e.lnglat;
        this.$emit("map-click", { lng, lat });
      });
    },

    // 添加标记点
    addMarkers() {
      const T = this.T;
      if (!T || !this.map) return;

      // 1. 先清理旧的覆盖物
      this.markerList.forEach((m) => this.map.removeOverLay(m));
      this.markerList = [];

      this.markers.forEach((item, index) => {
        const point = new T.LngLat(item.lng, item.lat);

        // 设置自定义图标
        let markerOpts = {};
        if (item.icon) {
          const icon = new T.Icon({
            iconUrl: item.icon,
            iconSize: new T.Point(32, 32),
            iconAnchor: new T.Point(16, 32),
          });
          markerOpts.icon = icon;
        }

        const marker = new T.Marker(point, markerOpts);
        this.map.addOverLay(marker);

        // 使用传入的 data 动态生成 content
        const content = this.buildInfoWindowContent(item || {});

        const infoWindow = new T.InfoWindow(content, {
          offset: new T.Point(0, -20),
        });

        this.map.openInfoWindow(infoWindow, point);

        marker.addEventListener("click", () => {
          this.map.openInfoWindow(infoWindow, point);
        });

        this.markerList.push(marker);
        // 如果需要地图跟着移动到新点，可以打开下面这一行：
        if (index === 0) this.map.panTo(point);
      });
    },

    // 构造 InfoWindow 内容
    buildInfoWindowContent(item) {
      const data = item.data || {};

      const leaseTypeDict = [
        { value: "1", note: "房屋" },
        { value: "2", note: "场地" },
      ];

      const decorateDict = [
        { value: "1", note: "精装" },
        { value: "2", note: "毛坯" },
        { value: "3", note: "简装" },
        { value: "4", note: "拎包入住" },
      ];

      const leaseUseTypeDict = [
        { value: "1", note: "商业" },
        { value: "2", note: "楼宇办公" },
        { value: "8", note: "园区" },
        { value: "16", note: "仓储" },
        { value: "32", note: "厂房" },
        { value: "64", note: "酒店" },
        { value: "256", note: "住宅" },
        { value: "512", note: "公寓" },
        { value: "1024", note: "工业" },
        { value: "2048", note: "农用" },
        { value: "4096", note: "市政配套" },
        { value: "8192", note: "场地" },
        { value: "16384", note: "其他" },
      ];

      const leaseTypeText = this.getArrayDictNote(
        leaseTypeDict,
        data.leaseType,
        "房屋"
      );

      const decorateText = this.getDictNote(
        decorateDict,
        data.currentSituationDecoration,
        "简装"
      );

      const leaseUseTypeText = this.getArrayDictNote(
        leaseUseTypeDict,
        data.leaseUseType,
        "商业"
      );
      return `
      <div style="width:100%;display:flex;flex-direction:row;align-items:flex-start;">
        <div style="flex:0 0 100px; margin-right:10px;">
          <img src="${item.housePic || "/images/default-house.png"}" 
              style="width:100px;height:120px;object-fit:cover;border-radius:4px;" />
        </div>

        <div style="flex:1;display:flex;flex-direction:column;line-height:1.4;">
          <div style="font-weight:bold;font-family:Microsoft YaHei;font-size:14px;color:#484848;margin-bottom:4px;">
            ${data.address || "未知地址"}
          </div>

          <div style="display:flex;flex-direction:row;margin:4px 0 2px 0;">
            <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">租赁类型：</div>
            <div style="margin-top: 2px;">${leaseTypeText}</div>
          </div>

          <div style="display:flex;flex-direction:row;margin:2px 0;">
            <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">装修现状：</div>
            <div style="margin-top: 2px;">${decorateText}</div>
          </div>

          <div style="display:flex;flex-direction:row;margin:2px 0;">
            <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">租赁用途：</div>
            <div style="margin-top: 2px;">${leaseUseTypeText}</div>
          </div>
        </div>
      </div>
    `;
    },

    getDictNote(dict, value, defaultText = "") {
      if (value === undefined || value === null || value === "") {
        return defaultText;
      }

      const target = dict.find((item) => String(item.value) === String(value));
      return target ? target.note : defaultText;
    },

    getArrayDictNote(dict, values, defaultText = "") {
      if (!Array.isArray(values) || values.length === 0) {
        return defaultText;
      }

      const strValues = values.map(String);

      const result = dict
        .filter((item) => strValues.includes(String(item.value)))
        .map((item) => item.note);

      return result.length ? result.join("、") : defaultText;
    },
  },
  watch: {
    markers: {
      handler() {
        // 地图尚未初始化时，不处理，等 mounted/initMap 后再统一画一次
        if (!this.map || !this.T) return;
        this.addMarkers();
      },
      deep: true,
      immediate: true, // 父组件一进来就会根据初始 markers 画一次
    },
  },
};
</script>
